import type {
  ApiError,
  BricksConfig,
  Bump,
  ConfigResponse,
  Customer,
  LinksConfig,
  PaymentRequest,
  PaymentResult,
} from '../shared/types'
import type { BrickController, BricksBuilder, PaymentBrickSettings } from './mercadopago'

// Ids de los bumps tildados. Arranca vacío: nada preseleccionado.
const selectedBumps = new Set<string>()

// Lo fija init() con lo que devuelve /api/config.
let currencySymbol = '$'

// Qué hacer cuando cambia la selección de bumps. Lo define cada modo de checkout.
let onSelectionChange: () => void = () => {}

void init()

async function init(): Promise<void> {
  try {
    const response = await fetch('/api/config', { headers: { accept: 'application/json' } })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const config = (await response.json()) as ConfigResponse
    currencySymbol = config.currencySymbol

    renderProduct(config)
    renderBumps(config)
    renderWhatsapp(config)
    renderSummary(config)

    // El HTML viene armado para el modo 'links'; 'mercadopago' es el que
    // reacomoda la página.
    if (config.mode === 'mercadopago') {
      setupBricks(config)
    } else {
      setupLinks(config)
    }
  } catch (cause) {
    console.error('No se pudo cargar /api/config:', cause)
    requireEl('payment-hint').textContent =
      'Ocurrió un error al cargar la página. Recargá e intentá de nuevo.'
  }
}

// ---- Producto ----
function renderProduct(config: ConfigResponse): void {
  requireEl('product-title').textContent = config.product.title
  requireEl('product-description').textContent = config.product.description
  requireEl('product-price').textContent = money(config.product.price)
}

// ---- Bumps ----
function renderBumps(config: ConfigResponse): void {
  if (!config.bumps.length) {
    requireEl('bumps-card').hidden = true
    return
  }

  requireEl('bumps-list').replaceChildren(
    ...config.bumps.map((bump) => createBumpRow(bump, config)),
  )
}

function createBumpRow(bump: Bump, config: ConfigResponse): HTMLLabelElement {
  const label = createEl('label', 'bump')

  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.addEventListener('change', () => {
    if (checkbox.checked) {
      selectedBumps.add(bump.id)
    } else {
      selectedBumps.delete(bump.id)
    }
    label.classList.toggle('selected', checkbox.checked)
    renderSummary(config)
    onSelectionChange()
  })

  const image = createEl('img', 'bump-img')
  image.src = bump.image
  image.alt = ''
  image.loading = 'lazy'
  // Si el asset no está, mejor la fila sin miniatura que el ícono de imagen rota.
  image.addEventListener('error', () => image.remove())

  const pricing = createEl('div', 'bump-pricing')
  pricing.append(
    createEl('span', 'bump-price-old', money(bump.original_price)),
    createEl('span', 'bump-price', `+ ${money(bump.price)}`),
  )

  const body = createEl('div', 'bump-body')
  body.append(
    createEl('div', 'bump-title', bump.title),
    createEl('div', 'bump-desc', bump.description),
    pricing,
  )

  label.append(checkbox, image, body)
  return label
}

function chosenBumps(config: ConfigResponse): Bump[] {
  return config.bumps.filter((bump) => selectedBumps.has(bump.id))
}

function total(config: ConfigResponse): number {
  return chosenBumps(config).reduce((sum, bump) => sum + bump.price, config.product.price)
}

// ---- Resumen ----
function renderSummary(config: ConfigResponse): void {
  const items = [{ title: config.product.title, price: config.product.price }, ...chosenBumps(config)]

  requireEl('summary-items').replaceChildren(
    ...items.map((item) => {
      const li = document.createElement('li')
      li.append(createEl('span', '', item.title), createEl('span', '', money(item.price)))
      return li
    }),
  )

  requireEl('summary-total').textContent = money(total(config))
}

// =====================================================================
// Modo 'links': el CTA apunta a un link de pago fijo
// =====================================================================
function setupLinks(config: LinksConfig): void {
  onSelectionChange = () => updateCheckout(config)
  bindCheckout()
  updateCheckout(config)
}

/** `main_only` sin bumps, `main_<id>` o `main_<id>_<id>` según lo tildado. */
function paymentKey(config: LinksConfig): string {
  const ids = chosenBumps(config).map((bump) => bump.id)
  return ids.length ? `main_${ids.join('_')}` : 'main_only'
}

function updateCheckout(config: LinksConfig): void {
  const button = requireEl<HTMLAnchorElement>('checkout-button')
  const hint = requireEl('payment-hint')
  const key = paymentKey(config)

  // PaymentLinks tiene las cuatro combinaciones posibles, pero la key se arma en
  // runtime: si el config queda desactualizado, queremos enterarnos y no romper.
  const link = Object.entries(config.payment_links).find(([name]) => name === key)?.[1]

  if (link) {
    button.href = link
    button.classList.remove('disabled')
    button.removeAttribute('aria-disabled')
    hint.textContent = ''
    return
  }

  console.warn(`No hay payment_links["${key}"] en el config.`)
  button.removeAttribute('href')
  button.classList.add('disabled')
  button.setAttribute('aria-disabled', 'true')
  hint.textContent =
    'Esta combinación no está disponible por ahora. Escribinos por WhatsApp y te ayudamos.'
}

function bindCheckout(): void {
  const button = requireEl<HTMLAnchorElement>('checkout-button')
  button.addEventListener('click', (event) => {
    if (!button.getAttribute('href')) event.preventDefault()
  })
}

// =====================================================================
// Modo 'mercadopago': Payment Brick
// =====================================================================
const SDK_URL = 'https://sdk.mercadopago.com/js/v2'
/** Cuánto esperamos después de la última tecla antes de montar el brick. */
const MOUNT_DEBOUNCE_MS = 600

let builder: BricksBuilder | null = null
let brick: BrickController | null = null
/** Datos del payer con los que se montó el brick; si cambian, hay que remontarlo. */
let mountedFor: string | null = null
let mounting = false
let mountTimer: number | undefined

function setupBricks(config: BricksConfig): void {
  // El CTA a links no existe en este modo.
  requireEl('checkout-button').hidden = true
  requireEl('payment-hint').hidden = true
  requireEl('customer-card').hidden = false
  requireEl('payment-card').hidden = false

  onSelectionChange = () => void syncAmount(config)

  if (!config.mercadopago.publicKey) {
    console.error('Falta MP_PUBLIC_KEY: no se puede montar el Payment Brick.')
    requireEl('payment-card').classList.remove('loading')
    updateGate(false)
    setStatus('error', 'El pago online no está disponible ahora. Escribinos por WhatsApp y te ayudamos.')
    return
  }

  const form = requireEl<HTMLFormElement>('customer-form')
  // El form no se envía: el submit real lo maneja el brick.
  form.addEventListener('submit', (event) => event.preventDefault())

  // 'input' abre/cierra el brick mientras se escribe (con debounce, para no
  // remontarlo con un e-mail a medio tipear); 'change' confirma al salir del campo.
  form.addEventListener('input', () => {
    updateGate(readCustomer() !== null)
    window.clearTimeout(mountTimer)
    mountTimer = window.setTimeout(() => void ensureBrick(config), MOUNT_DEBOUNCE_MS)
  })
  form.addEventListener('change', () => {
    updateGate(readCustomer() !== null)
    window.clearTimeout(mountTimer)
    void ensureBrick(config)
  })

  for (const input of customerInputs()) {
    input.addEventListener('blur', () => {
      input.classList.toggle('invalid', input.value.trim() !== '' && !fieldValid(input))
    })
  }

  updateGate(readCustomer() !== null)
  void ensureBrick(config)
}

/** Habilita o apaga la sección de pago según si los datos de contacto están completos. */
function updateGate(ready: boolean): void {
  requireEl('payment-card').classList.toggle('disabled', !ready)
  requireEl('brick-hint').textContent = ready
    ? 'Elegí cómo querés pagar.'
    : 'Completá tus datos arriba para habilitar el pago.'
}

/** Monta el brick (o lo remonta si cambiaron los datos del payer). */
async function ensureBrick(config: BricksConfig): Promise<void> {
  const customer = readCustomer()
  if (!customer || mounting) return

  const signature = signatureOf(customer)
  if (brick && signature === mountedFor) return

  mounting = true
  try {
    const bricks = await loadBuilder(config)
    brick?.unmount()
    brick = null
    brick = await bricks.create('payment', 'paymentBrick_container', brickSettings(config, customer))
    mountedFor = signature
  } catch (cause) {
    console.error('No se pudo montar el Payment Brick:', cause)
    setStatus(
      'error',
      'No pudimos cargar el formulario de pago. Recargá la página o escribinos por WhatsApp.',
    )
  } finally {
    mounting = false
  }

  // Si mientras montábamos siguieron escribiendo, los datos que quedaron en el
  // brick ya no son los del form: se vuelve a intentar con los nuevos.
  if (readCustomer() !== null && mountedFor !== signatureOf(readCustomer())) {
    window.clearTimeout(mountTimer)
    mountTimer = window.setTimeout(() => void ensureBrick(config), MOUNT_DEBOUNCE_MS)
  }
}

/** Los datos del payer que quedan "horneados" en el brick al montarlo. */
function signatureOf(customer: Customer | null): string | null {
  return customer && `${customer.email}|${customer.name}`
}

function brickSettings(config: BricksConfig, customer: Customer): PaymentBrickSettings {
  const [firstName = '', ...rest] = customer.name.split(/\s+/)

  return {
    initialization: {
      amount: total(config),
      payer: { email: customer.email, firstName, lastName: rest.join(' ') },
    },
    customization: {
      visual: { style: { theme: 'dark' } },
      // Sin `mercadoPago`: el pago con dinero en cuenta necesita una preference,
      // y acá cobramos directo contra /v1/payments.
      paymentMethods: {
        creditCard: 'all',
        debitCard: 'all',
        maxInstallments: 12,
      },
    },
    callbacks: {
      onReady: () => requireEl('payment-card').classList.remove('loading'),
      onError: (error) => console.error('Payment Brick:', error),
      onSubmit: (data) => submitPayment(config, data.formData),
    },
  }
}

/**
 * Cambió la selección de bumps: hay que reflejar el nuevo total en el brick.
 * `update()` no está en todas las versiones del SDK, así que si falla lo remontamos.
 */
async function syncAmount(config: BricksConfig): Promise<void> {
  if (!brick) return

  const amount = total(config)
  try {
    if (typeof brick.update === 'function') {
      await brick.update({ amount })
      return
    }
  } catch (cause) {
    console.warn('El brick no aceptó el monto nuevo, se remonta:', cause)
  }

  mountedFor = null
  await ensureBrick(config)
}

/**
 * `onSubmit` del brick. Resolver = pago tomado (aprobado o pendiente);
 * rechazar = el brick deshabilita el loading y permite reintentar.
 */
async function submitPayment(
  config: BricksConfig,
  formData: Record<string, unknown>,
): Promise<void> {
  const customer = readCustomer()
  if (!customer) {
    setStatus('error', 'Completá tus datos de contacto antes de pagar.')
    throw new Error('Datos de contacto incompletos')
  }

  setStatus('info', 'Procesando el pago…')

  const payload: PaymentRequest = {
    bumps: chosenBumps(config).map((bump) => bump.id),
    customer,
    formData,
  }

  let result: PaymentResult
  try {
    const response = await fetch('/api/payments', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(payload),
    })
    const data: unknown = await response.json()
    if (!response.ok) {
      throw new Error((data as ApiError).message || `HTTP ${response.status}`)
    }
    result = data as PaymentResult
  } catch (cause) {
    console.error('Falló POST /api/payments:', cause)
    setStatus(
      'error',
      'No pudimos comunicarnos con el procesador de pagos. Revisá tu conexión e intentá de nuevo.',
    )
    throw cause instanceof Error ? cause : new Error('Falló el cobro')
  }

  // El `status_detail` de MP es lo único que explica un rechazo; el mensaje que
  // ve el usuario es una traducción y pierde el detalle.
  console.info(`Pago ${result.id}: ${result.status} / ${result.statusDetail}`)

  setStatus(STATUS_KIND[result.outcome], result.message)
  requireEl('payment-status').scrollIntoView({ behavior: 'smooth', block: 'center' })

  if (result.outcome === 'rejected') {
    // Rechazar deja el brick usable para que puedan probar con otra tarjeta.
    throw new Error(`Pago rechazado (${result.statusDetail})`)
  }

  // Pago tomado: no tiene sentido seguir editando la compra.
  lockPurchase()
}

const STATUS_KIND = {
  approved: 'ok',
  pending: 'pending',
  rejected: 'error',
} as const

function lockPurchase(): void {
  brick?.unmount()
  brick = null
  mountedFor = null

  requireEl('brick-hint').hidden = true
  requireEl('customer-card').hidden = true
  for (const checkbox of requireEl('bumps-list').querySelectorAll('input')) {
    checkbox.disabled = true
  }
}

function setStatus(kind: 'info' | 'ok' | 'pending' | 'error', text: string): void {
  const el = requireEl('payment-status')
  el.className = `payment-status ${kind}`
  el.textContent = text
}

/** Carga el SDK bajo demanda: en modo 'links' la página no lo necesita. */
async function loadBuilder(config: BricksConfig): Promise<BricksBuilder> {
  if (builder) return builder

  if (!window.MercadoPago) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SDK_URL
      script.async = true
      script.addEventListener('load', () => resolve(), { once: true })
      script.addEventListener('error', () => reject(new Error(`No se pudo cargar ${SDK_URL}`)), {
        once: true,
      })
      document.head.append(script)
    })
  }

  const MercadoPago = window.MercadoPago
  if (!MercadoPago) throw new Error('El SDK de Mercado Pago cargó pero no expuso window.MercadoPago')

  builder = new MercadoPago(config.mercadopago.publicKey, {
    locale: config.mercadopago.locale,
  }).bricks()
  return builder
}

// ---- Datos de contacto ----
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function customerInputs(): HTMLInputElement[] {
  return ['name', 'whatsapp', 'email'].map((id) => requireEl<HTMLInputElement>(id))
}

function fieldValid(input: HTMLInputElement): boolean {
  const value = input.value.trim()
  if (input.id === 'email') return EMAIL_RE.test(value)
  if (input.id === 'whatsapp') return value.replace(/\D/g, '').length >= 8
  return value.length >= 3
}

/** Los tres campos completos y válidos, o `null`. Mismas reglas que valida el server. */
function readCustomer(): Customer | null {
  const inputs = customerInputs()
  if (!inputs.every(fieldValid)) return null

  return {
    name: requireEl<HTMLInputElement>('name').value.trim(),
    whatsapp: requireEl<HTMLInputElement>('whatsapp').value.trim(),
    email: requireEl<HTMLInputElement>('email').value.trim(),
  }
}

// ---- WhatsApp ----
function renderWhatsapp(config: ConfigResponse): void {
  const value = config.whatsapp.trim()
  if (!value) return

  // El config admite tanto un link completo (wa.link/…) como un número suelto.
  requireEl<HTMLAnchorElement>('whatsapp-float').href = /^https?:\/\//i.test(value)
    ? value
    : `https://wa.me/${value.replace(/\D/g, '')}`
}

// ---- Helpers ----
function money(value: number): string {
  return `${currencySymbol}${value.toLocaleString('es-UY')}`
}

function createEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag)
  if (className) el.className = className
  if (text !== undefined) el.textContent = text
  return el
}

function requireEl<T extends HTMLElement = HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`No se encontró el elemento "#${id}"`)
  return el as T
}
